import React, { Suspense, lazy, useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { ArrowCircleDown } from '@mui/icons-material'
import CloseIcon from '@mui/icons-material/Close'
import SendIcon from '@mui/icons-material/Send'
import TranslateIcon from '@mui/icons-material/Translate'
import {
  Box,
  CircularProgress,
  Drawer,
  FormControlLabel,
  IconButton,
  Link,
  List,
  ListItem,
  ListItemText,
  Paper,
  Switch,
  Typography,
} from '@mui/material'

import { apiZodios } from '~api/apiZodios'
import { Avatar, TextField } from '~components/common'
import { ILocationEntryInlineAddress } from '~components/common/LocationEntryInline'
import { IS_DEMO, IS_PROD } from '~config'
import { lightTheme } from '~createTheme'
import {
  PreTextStreamResponseStep,
  StreamResponseStep,
  preTextStreamResponseSteps,
  useStreamAIResponse,
} from '~hooks/useStreamAIResponse'
import { RootState } from '~redux/rootReducer'
import {
  AIInteractionType,
  AIInteractionTypeEnum,
  EnrichedOrganizationAISettings,
  GeoPoint,
  IAIInteractionMessageDisplay,
  ResponseTypeEnum,
} from '~types'
import { processResponseStream } from '~utils/responseStreamUtils'

import { convertToLogMessages } from './AIInteractionLogContent'
import ChatBubbleProgressIndicator, { ChatBubbleLoadingState } from './ChatBubbleProgressIndicator'
import { ChatStreamProgressBubble } from './ChatStreamProgressBubble'
import { MessageBubble } from './MessageBubble'

const LocationEntryInline = lazy(() => import('~components/common/LocationEntryInline'))

interface AIInteractionViewProps {
  orgAiSettings: EnrichedOrganizationAISettings
  testing: boolean
  initialAiInteractionId?: string
  interactionType?: AIInteractionTypeEnum
  initialMessage?: string
}

type LanguageEntry = {
  english: string
  native: string
  code: string
  onClick?: () => void
}

interface ISubmitUserMessageParams {
  text: string
  location?: GeoPoint
}

const SUPPORTED_LANGUAGES: LanguageEntry[] = [
  { english: 'Afrikaans', native: 'Afrikaans', code: 'af' },
  { english: 'Albanian', native: 'Shqip', code: 'sq' },
  { english: 'Amharic', native: 'አማርኛ', code: 'am' },
  { english: 'Arabic', native: 'العربية', code: 'ar' },
  { english: 'Armenian', native: 'Հայերեն', code: 'hy' },
  { english: 'Azerbaijani', native: 'Azərbaycan', code: 'az' },
  { english: 'Bangla', native: 'বাংলা', code: 'bn' },
  { english: 'Bosnian', native: 'Bosanski', code: 'bs' },
  { english: 'Bulgarian', native: 'Български', code: 'bg' },
  { english: 'Catalan', native: 'Català', code: 'ca' },
  { english: 'Chinese (Simplified)', native: '简体中文', code: 'zh' },
  { english: 'Chinese (Traditional)', native: '繁體中文', code: 'zh-TW' },
  { english: 'Croatian', native: 'Hrvatski', code: 'hr' },
  { english: 'Czech', native: 'Čeština', code: 'cs' },
  { english: 'Danish', native: 'Dansk', code: 'da' },
  { english: 'Dari', native: 'دری', code: 'fa-AF' },
  { english: 'Dutch', native: 'Nederlands', code: 'nl' },
  { english: 'English', native: 'English', code: 'en' },
  { english: 'Estonian', native: 'Eesti', code: 'et' },
  { english: 'Farsi (Persian)', native: 'فارسی', code: 'fa' },
  { english: 'Filipino', native: 'Tagalog', code: 'tl' },
  { english: 'Finnish', native: 'Suomi', code: 'fi' },
  { english: 'French', native: 'Français', code: 'fr' },
  { english: 'French (Canada)', native: 'Français (Canada)', code: 'fr-CA' },
  { english: 'Georgian', native: 'ქართული', code: 'ka' },
  { english: 'German', native: 'Deutsch', code: 'de' },
  { english: 'Greek', native: 'Ελληνικά', code: 'el' },
  { english: 'Gujarati', native: 'ગુજરાતી', code: 'gu' },
  { english: 'Haitian Creole', native: 'Kreyòl Ayisyen', code: 'ht' },
  { english: 'Hausa', native: 'Hausa', code: 'ha' },
  { english: 'Hebrew', native: 'עברית', code: 'he' },
  { english: 'Hindi', native: 'हिन्दी', code: 'hi' },
  { english: 'Hungarian', native: 'Magyar', code: 'hu' },
  { english: 'Icelandic', native: 'Íslenska', code: 'is' },
  { english: 'Indonesian', native: 'Bahasa Indonesia', code: 'id' },
  { english: 'Irish', native: 'Gaeilge', code: 'ga' },
  { english: 'Italian', native: 'Italiano', code: 'it' },
  { english: 'Japanese', native: '日本語', code: 'ja' },
  { english: 'Kannada', native: 'ಕನ್ನಡ', code: 'kn' },
  { english: 'Kazakh', native: 'Қазақ', code: 'kk' },
  { english: 'Korean', native: '한국어', code: 'ko' },
  { english: 'Latvian', native: 'Latviešu', code: 'lv' },
  { english: 'Lithuanian', native: 'Lietuvių', code: 'lt' },
  { english: 'Macedonian', native: 'Македонски', code: 'mk' },
  { english: 'Malay', native: 'Bahasa Melayu', code: 'ms' },
  { english: 'Malayalam', native: 'മലയാളം', code: 'ml' },
  { english: 'Maltese', native: 'Malti', code: 'mt' },
  { english: 'Marathi', native: 'मराठी', code: 'mr' },
  { english: 'Mongolian', native: 'Монгол', code: 'mn' },
  { english: 'Norwegian (Bokmål)', native: 'Norsk Bokmål', code: 'no' },
  { english: 'Pashto', native: 'پښتو', code: 'ps' },
  { english: 'Polish', native: 'Polski', code: 'pl' },
  { english: 'Portuguese (Brazil)', native: 'Português (Brasil)', code: 'pt' },
  { english: 'Portuguese (Portugal)', native: 'Português (Portugal)', code: 'pt-PT' },
  { english: 'Punjabi', native: 'ਪੰਜਾਬੀ', code: 'pa' },
  { english: 'Romanian', native: 'Română', code: 'ro' },
  { english: 'Russian', native: 'Русский', code: 'ru' },
  { english: 'Serbian', native: 'Српски', code: 'sr' },
  { english: 'Sinhala', native: 'සිංහල', code: 'si' },
  { english: 'Slovak', native: 'Slovenčina', code: 'sk' },
  { english: 'Slovenian', native: 'Slovenščina', code: 'sl' },
  { english: 'Somali', native: 'Soomaali', code: 'so' },
  { english: 'Spanish', native: 'Español', code: 'es' },
  { english: 'Spanish (Mexico)', native: 'Español (México)', code: 'es-MX' },
  { english: 'Swahili', native: 'Kiswahili', code: 'sw' },
  { english: 'Swedish', native: 'Svenska', code: 'sv' },
  { english: 'Tamil', native: 'தமிழ்', code: 'ta' },
  { english: 'Telugu', native: 'తెలుగు', code: 'te' },
  { english: 'Thai', native: 'ไทย', code: 'th' },
  { english: 'Turkish', native: 'Türkçe', code: 'tr' },
  { english: 'Ukrainian', native: 'Українська', code: 'uk' },
  { english: 'Urdu', native: 'اردو', code: 'ur' },
  { english: 'Uzbek', native: "O'zbek", code: 'uz' },
  { english: 'Vietnamese', native: 'Tiếng Việt', code: 'vi' },
  { english: 'Welsh', native: 'Cymraeg', code: 'cy' },
].sort((a, b) => a.english.localeCompare(b.english))

interface LanguagePromptProps {
  orgAiSettings: EnrichedOrganizationAISettings
  onShowMore: () => void
}

const LanguagePrompt: React.FC<LanguagePromptProps> = ({ orgAiSettings, onShowMore }) => (
  <Paper
    sx={{
      position: 'relative',
      maxWidth: '85%',
      alignSelf: 'flex-start',
      backgroundColor: 'background.paper',
      color: 'text.primary',
      borderRadius: `${orgAiSettings.corner_radius ?? 8}px`,
      boxShadow: 'none',
      p: 2,
      mb: '2px',
    }}
  >
    {orgAiSettings.language_support_text ? (
      <Typography
        fontFamily={orgAiSettings.font_family || 'inherit'}
        variant='body2'
        sx={{ fontSize: '16px' }}
      >
        {orgAiSettings.language_support_text}
      </Typography>
    ) : (
      <Typography
        fontFamily={orgAiSettings.font_family || 'inherit'}
        variant='body2'
        sx={{ fontSize: '16px' }}
      >
        Ask in your language of choice and we'll translate your response. Languages supported
        include: English, Español, 简体中文, Tiếng Việt, Tagalog, العربية.{' '}
        <Link
          component='button'
          variant='body2'
          onClick={onShowMore}
          sx={{
            ...lightTheme.typography.link,
            fontSize: '16px',
            textDecoration: 'underline',
            fontFamily: orgAiSettings.font_family || 'inherit',
          }}
        >
          Show all supported languages
        </Link>
      </Typography>
    )}
  </Paper>
)

const AIInteractionView: React.FC<AIInteractionViewProps> = ({
  orgAiSettings,
  testing,
  initialAiInteractionId,
  interactionType = AIInteractionType.enum.CHAT,
  initialMessage,
}) => {
  const isSuper = useSelector((state: RootState) => state?.auth?.user?.super)
  const userIdFromState = useSelector((state: RootState) => state?.auth.userId)
  const [messages, setMessages] = useState<IAIInteractionMessageDisplay[]>(() => {
    const aiDisclaimerMessage: IAIInteractionMessageDisplay = {
      id: null,
      text:
        orgAiSettings.disclaimer_text ||
        'AI can make mistakes. Please confirm important information.',
      sender: 'AI',
      timestamp: new Date(),
      impression: null,
    }
    // If we have an initial AI interaction ID, start with empty messages
    // They will be loaded in the useEffect
    if (initialAiInteractionId) {
      return []
    }
    const initialMessage: IAIInteractionMessageDisplay = {
      id: null,
      text: orgAiSettings.chat_greeting || 'Hi, how can I help?',
      sender: 'AI',
      timestamp: new Date(),
      impression: null,
    }
    return [aiDisclaimerMessage, initialMessage]
  })
  const [inputValue, setInputValue] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [isResponseComplete, setIsResponseComplete] = useState(false)
  const [isAddressWidgetOpen, setIsAddressWidgetOpen] = useState(false)
  const [userId, setUserId] = useState<string | undefined>(undefined)
  const [aiInteractionId, setAIInteractionId] = useState<string | undefined>(initialAiInteractionId)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const latestMessageRef = useRef<HTMLDivElement>(null)
  const [languageDrawerOpen, setLanguageDrawerOpen] = useState(false)
  const [selectedLanguage, setSelectedLanguage] = useState<string>('en')
  const [loadingState, setLoadingState] = useState<ChatBubbleLoadingState>('analyzing')
  const [initialLocation, setInitialLocation] = useState<GeoPoint | null>(null)
  const [streamStep, setStreamStep] = useState<StreamResponseStep>()

  const isPreTextStreamStep = Boolean(
    streamStep && (preTextStreamResponseSteps as readonly string[]).includes(streamStep),
  )

  const [debugVisible, setDebugVisible] = useState<boolean>(true)
  const showDebugInfo = testing || (isSuper && debugVisible) || (!isSuper && !IS_DEMO && !IS_PROD)

  // HACK - Don't link back to Polimorphic.com for Morris County Surrogate
  const organizationId = orgAiSettings.organization_id
  const morrisCountySurrogateOrganizationIdProd = 'ffa09fe1-aaea-4984-9e13-9fa483857e2b'
  const includePolimorphicLink = organizationId !== morrisCountySurrogateOrganizationIdProd
  const DEFAULT_LOCATION = { lat: 40.712776, lon: -74.005974 } // Default to New York City

  const scrollToBottom = (): void => {
    messagesEndRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'nearest',
    })
  }

  const loadAIInteractionHistory = async (): Promise<void> => {
    if (!userIdFromState) return

    if (!initialAiInteractionId) {
      const initialMessage: IAIInteractionMessageDisplay = {
        id: null,
        text: orgAiSettings.chat_greeting || 'Hi, how can I help?',
        sender: 'AI',
        timestamp: new Date(),
        impression: null,
      }
      setMessages([initialMessage])
      return
    }

    try {
      const log = await apiZodios.ai_getAIInteractionLog({
        user_id: userIdFromState,
        ai_interaction_id: initialAiInteractionId,
      })

      const convertedMessages = convertToLogMessages(log.interaction_log, false)
      setMessages(convertedMessages)

      setUserId(userIdFromState)
    } catch (error) {
      console.error('Error loading AI interaction history:', error)
      const initialMessage: IAIInteractionMessageDisplay = {
        id: null,
        text: orgAiSettings.chat_greeting || 'Hi, how can I help?',
        sender: 'AI',
        timestamp: new Date(),
        impression: null,
      }
      setMessages([initialMessage])
    }
  }

  useEffect(() => {
    if (orgAiSettings?.organization_location) {
      setInitialLocation(orgAiSettings.organization_location)
    } else {
      setInitialLocation(null)
    }
  }, [orgAiSettings])

  useEffect(() => {
    loadAIInteractionHistory()
  }, [initialAiInteractionId, userIdFromState, orgAiSettings])

  const hasSubmittedInitialMessage = useRef(false)
  useEffect(() => {
    if (
      initialMessage &&
      !initialAiInteractionId &&
      !hasSubmittedInitialMessage.current &&
      !isLoading
    ) {
      hasSubmittedInitialMessage.current = true
      setTimeout(() => {
        submitMessage({ text: initialMessage })
      }, 100)
    }
  }, [initialMessage, initialAiInteractionId, isLoading])

  const submitMessage = async ({ text, location }: ISubmitUserMessageParams): Promise<void> => {
    if (!text.trim() || isLoading) return

    const userMessage: IAIInteractionMessageDisplay = {
      id: null,
      text: text,
      sender: 'USER',
      timestamp: new Date(),
      impression: null,
    }

    setMessages((prev) => [...prev, userMessage])
    setInputValue('')
    setIsAddressWidgetOpen(false)
    setIsLoading(true)
    setLoadingState('analyzing')

    const retrievalTimer = setTimeout(() => setLoadingState('retrieving'), 2500)
    const generatingTimer = setTimeout(() => setLoadingState('generating'), 5000)
    const waitingTimer = setTimeout(() => setLoadingState('waiting'), 10000)

    try {
      const result = await apiZodios.ai_generateResponse({
        user_id: userId || undefined,
        ai_interaction_id: aiInteractionId || undefined,
        organization_id: orgAiSettings.organization_id,
        interaction_type: interactionType,
        query: text,
        location: location,
      })

      setIsResponseComplete(true)
      clearTimeout(retrievalTimer)
      clearTimeout(generatingTimer)
      clearTimeout(waitingTimer)

      const aiMessage: IAIInteractionMessageDisplay = {
        id: result.response_id,
        text: result.response_text,
        sender: 'AI',
        sources: result.sources,
        responseType: result.response_type || ResponseTypeEnum.Enum.ANSWER,
        timestamp: new Date(),
        impression: null,
      }

      if (result.response_type === ResponseTypeEnum.Enum.REQUEST_LOCATION) {
        setIsAddressWidgetOpen(true)
      } else {
        setIsAddressWidgetOpen(false)
      }

      setTimeout(() => {
        userMessage.queryAnalysis = result.query_analysis
        setMessages((prev) => [...prev.slice(0, -1), userMessage])
        setMessages((prev) => [...prev, aiMessage])
        setAIInteractionId(aiInteractionId || result.ai_interaction_id)
        setUserId(result.user_id || '')
        setIsLoading(false)
        setIsResponseComplete(false)
      }, 500)
    } catch (error) {
      console.error('Error generating AI response:', error)
      const errorMessage: IAIInteractionMessageDisplay = {
        id: null,
        text: 'I apologize, but I encountered an error processing your request. Please try again.',
        sender: 'AI',
        timestamp: new Date(),
        responseType: ResponseTypeEnum.Enum.ANSWER,
        impression: null,
      }
      setMessages((prev) => [...prev, errorMessage])
      clearTimeout(retrievalTimer)
      clearTimeout(generatingTimer)
      clearTimeout(waitingTimer)
      setIsLoading(false)
      setIsAddressWidgetOpen(false)
    }
  }

  const { isGeneratingResponseStream, streamAIResponse } = useStreamAIResponse(
    (response, receivedFirstResponseChunk) => {
      setStreamStep(response.step)

      // No response message to render
      if (response.step !== 'partial_response' && response.step !== 'complete_response') {
        return
      }

      // Add query analysis to query message when available
      if (response.query_analysis) {
        setMessages((prev) => {
          prev[prev.length - 2].queryAnalysis = response.query_analysis
          return prev
        })
      }

      const aiMessage: IAIInteractionMessageDisplay = {
        id: response.response_id ?? null,
        text: processResponseStream(response.response_text as string),
        sender: 'AI',
        sources: response.sources,
        responseType: ResponseTypeEnum.Enum.ANSWER,
        timestamp: new Date(),
        impression: null,
      }
      // Update partial response message if already received
      if (receivedFirstResponseChunk) {
        setMessages((prev) => [...prev.slice(0, -1), aiMessage])
      } else {
        setMessages((prev) => [...prev, aiMessage])
      }
    },
    (errorMessage) => {
      setMessages((prev) => [
        ...prev,
        {
          id: null,
          text: errorMessage,
          sender: 'AI',
          timestamp: new Date(),
          responseType: ResponseTypeEnum.Enum.ANSWER,
          impression: null,
        },
      ])
    },
  )

  const submitMessageStreaming = async ({ text }: ISubmitUserMessageParams): Promise<void> => {
    if (!text.trim()) return

    const userMessage: IAIInteractionMessageDisplay = {
      id: null,
      text: text,
      sender: 'USER',
      timestamp: new Date(),
      impression: null,
    }
    setMessages((prev) => [...prev, userMessage])
    setInputValue('')

    streamAIResponse({
      user_id: userId || undefined,
      organization_id: orgAiSettings.organization_id,
      interaction_type: interactionType,
      query: text,
    })
  }

  // Text submission handler
  const handleSubmit = async (e?: React.FormEvent): Promise<void> => {
    e?.preventDefault()
    if (orgAiSettings.stream_chat_responses) {
      submitMessageStreaming({ text: inputValue })
    } else {
      await submitMessage({ text: inputValue })
    }
  }

  useEffect(() => {
    // Only auto-scroll when not streaming
    if (!streamStep && !isGeneratingResponseStream && !isPreTextStreamStep) {
      scrollToBottom()
    }

    if (
      // Not streaming and response generated or streaming and response completely generated
      ((!streamStep && !isLoading) || streamStep === 'complete_response') &&
      messages.length > 0 &&
      messages[messages.length - 1].sender === 'AI' &&
      latestMessageRef.current
    ) {
      setTimeout(() => {
        if (latestMessageRef.current) {
          latestMessageRef.current.focus()
        }
      }, 50)
    }
  }, [messages, isLoading, streamStep, isGeneratingResponseStream, isPreTextStreamStep])

  const handleAddAddress = async (address: ILocationEntryInlineAddress): Promise<void> => {
    await submitMessage({
      text: address.address,
      location: address.location,
    })
  }

  const handleCancelAddAddress = (): void => {
    setIsAddressWidgetOpen(false)
    setInputValue('')
  }

  const translateGreeting = async (languageCode: string): Promise<void> => {
    try {
      const result = await apiZodios.ai_translateText({
        text: orgAiSettings.chat_greeting || 'Hi, how can I help?',
        source_language: 'en',
        target_language: languageCode,
      })

      setMessages((messages) => [
        {
          ...messages[0],
          text: result.translated_text,
        },
        ...messages.slice(1),
      ])

      setSelectedLanguage(languageCode)
    } catch (error) {
      console.error('Translation error:', error)
    }
  }

  const [showScrollToBottomButton, setShowScrollToBottomButton] = useState(false)
  useEffect(() => {
    const intersectionObserver = new IntersectionObserver(([entry]) =>
      setShowScrollToBottomButton(!entry.isIntersecting),
    )

    const currentRef = messagesEndRef.current
    if (currentRef) {
      intersectionObserver.observe(currentRef)
    }

    return (): void => intersectionObserver.disconnect()
  }, [])

  return (
    <Box
      sx={{
        height: '100%',
        flexGrow: 1,
        display: 'flex',
        flexDirection: 'column',
        fontFamily: orgAiSettings.font_family || 'inherit',
        position: 'relative',
      }}
    >
      <Box
        sx={{
          backgroundColor: orgAiSettings.bg_color ? `#${orgAiSettings.bg_color}` : undefined,
          padding: '15px',
          color: orgAiSettings.font_color ? `#${orgAiSettings.font_color}` : undefined,
          display: 'flex',
          alignItems: 'center',
          borderTopLeftRadius:
            interactionType === 'ASSISTANT' ? '0px' : `${orgAiSettings.corner_radius ?? 8}px`,
          borderTopRightRadius:
            interactionType === 'ASSISTANT' ? '0px' : `${orgAiSettings.corner_radius ?? 8}px`,
          gap: 2,
        }}
      >
        {(orgAiSettings.organization_ai_logo || orgAiSettings.organization_image) && (
          <Avatar
            src={
              orgAiSettings.organization_ai_logo ?? orgAiSettings.organization_image ?? undefined
            }
            size='40px'
            alt={orgAiSettings.organization_name || 'Organization Logo'}
          />
        )}
        <Typography fontFamily={orgAiSettings.font_family || 'inherit'} variant='h6'>
          {orgAiSettings.chat_name || `Chat with ${orgAiSettings.organization_name}`}
        </Typography>
        <Box sx={{ marginLeft: 'auto' }}>
          <IconButton onClick={() => setLanguageDrawerOpen(true)} aria-label='Supported Languages'>
            <TranslateIcon
              sx={{
                color: orgAiSettings.font_color ? `#${orgAiSettings.font_color}` : undefined,
              }}
            />
          </IconButton>
        </Box>
      </Box>

      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: 'var(--offwhite)',
          p: 2,
          position: 'relative',
        }}
        role='log'
        aria-label='Chat messages'
      >
        <LanguagePrompt
          orgAiSettings={orgAiSettings}
          onShowMore={() => setLanguageDrawerOpen(true)}
        />
        {messages.map((message, index) => {
          const isNextConsecutive =
            index < messages.length - 1 && messages[index + 1].sender === message.sender
          return (
            <MessageBubble
              key={message.id}
              message={message}
              orgAiSettings={orgAiSettings}
              showDebugInfo={showDebugInfo}
              isNextConsecutive={isNextConsecutive}
              ref={index === messages.length - 1 ? latestMessageRef : undefined}
              tabIndex={0}
              aria-setsize={messages.length}
              aria-posinset={index + 1}
            />
          )
        })}
        {isAddressWidgetOpen && (
          <Box sx={{ mt: 2, width: '100%', display: 'flex', justifyContent: 'center' }}>
            <Suspense fallback={<CircularProgress />}>
              <LocationEntryInline
                onSave={(address: ILocationEntryInlineAddress) => handleAddAddress(address)}
                onCancel={handleCancelAddAddress}
                initialPosition={initialLocation || DEFAULT_LOCATION}
              />
            </Suspense>
          </Box>
        )}
        {isLoading && (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', mb: 2 }}>
            <ChatBubbleProgressIndicator
              loadingState={loadingState}
              onComplete={isResponseComplete}
            />
          </Box>
        )}
        {(isGeneratingResponseStream || isPreTextStreamStep) && (
          <ChatStreamProgressBubble
            step={
              isGeneratingResponseStream
                ? 'initialization'
                : (streamStep as PreTextStreamResponseStep)
            }
          />
        )}
        <div ref={messagesEndRef} />
        <IconButton
          onClick={scrollToBottom}
          sx={{
            position: 'sticky',
            bottom: 16,
            alignSelf: 'flex-end',
            marginRight: 2,
            zIndex: 1,
            visibility: showScrollToBottomButton ? 'inherit' : 'hidden',
          }}
        >
          <ArrowCircleDown />
        </IconButton>
      </Box>

      <Paper
        component='form'
        onSubmit={handleSubmit}
        sx={{
          p: 2,
          pb: 1,
          borderTop: '1px solid #e0e0e0',
          borderRadius:
            interactionType === 'ASSISTANT' ? '0px' : `${orgAiSettings.corner_radius ?? 8}px`,
          borderTopRightRadius: 0,
          borderTopLeftRadius: 0,
        }}
      >
        <Box sx={{ width: '100%', display: 'flex', alignItems: 'center', gap: 1 }}>
          <TextField
            fullWidth
            multiline
            maxRows={4}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            placeholder='Type your message...'
            variant='outlined'
            disabled={isLoading}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault()
                handleSubmit()
              }
            }}
            sx={{
              borderRadius: `${orgAiSettings.corner_radius ?? 8}px`,
              '& .MuiInputBase-input, & .MuiFormLabel-root': {
                fontFamily: orgAiSettings.font_family || 'inherit',
              },
            }}
            inputRef={inputRef}
          />
          <IconButton
            sx={{
              backgroundColor: orgAiSettings.bg_color
                ? `#${orgAiSettings.bg_color}`
                : 'primary.dark',
              '&:hover': {
                backgroundColor: orgAiSettings.bg_color
                  ? `#${orgAiSettings.bg_color}`
                  : 'primary.dark',
                opacity: 0.9,
              },
              '&.Mui-disabled': {
                backgroundColor: orgAiSettings.bg_color
                  ? `#${orgAiSettings.bg_color}`
                  : 'primary.dark',
                opacity: 0.5,
              },
            }}
            onClick={() => handleSubmit()}
            disabled={isLoading || !inputValue.trim()}
            aria-label='Send message'
          >
            <SendIcon
              sx={{
                color: orgAiSettings.font_color
                  ? `#${orgAiSettings.font_color}`
                  : 'primary.contrastText',
              }}
            />
          </IconButton>
        </Box>
        {messages.length > 0 && !orgAiSettings.hide_privacy_policy && (
          <Box sx={{ mt: 2, textAlign: 'center' }}>
            <Typography
              fontFamily={orgAiSettings.font_family || 'inherit'}
              variant='body2'
              sx={{ color: '#757575' }}
            >
              Chat history is stored in accordance with our{' '}
              <Link
                href='https://www.polimorphic.com/privacy-policy'
                target='_blank'
                sx={{
                  color: '#757575',
                  textDecoration: 'underline',
                  fontFamily: orgAiSettings.font_family || 'inherit',
                }}
              >
                privacy policy
              </Link>
              {includePolimorphicLink ? <>. By Polimorphic.</> : null}
            </Typography>
          </Box>
        )}
        {messages.length > 0 && orgAiSettings.hide_privacy_policy && (
          <Box sx={{ mt: 2, textAlign: 'center' }}>
            <Typography
              fontFamily={orgAiSettings.font_family || 'inherit'}
              variant='body2'
              sx={{ color: '#757575' }}
            >
              Built By Polimorphic.
            </Typography>
          </Box>
        )}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mt: 1 }}>
          {isSuper && (
            <Box mr={2}>
              <FormControlLabel
                control={
                  <Switch
                    size='small'
                    checked={debugVisible}
                    onChange={(e) => setDebugVisible(e.target.checked)}
                    color='primary'
                  />
                }
                label={
                  <Typography sx={{ fontSize: '12px', fontFamily: 'monospace' }}>
                    Debug Info
                  </Typography>
                }
                sx={{ m: 0 }}
              />
            </Box>
          )}
        </Box>
      </Paper>

      <Drawer anchor='right' open={languageDrawerOpen} onClose={() => setLanguageDrawerOpen(false)}>
        <Box sx={{ width: 300, p: 2 }}>
          <Box
            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}
          >
            <Typography fontFamily={orgAiSettings.font_family || 'inherit'} variant='h6'>
              Supported Languages
            </Typography>
            <IconButton
              onClick={() => setLanguageDrawerOpen(false)}
              aria-label='Close supported languages'
            >
              <CloseIcon />
            </IconButton>
          </Box>
          <Typography
            fontFamily={orgAiSettings.font_family || 'inherit'}
            variant='body1'
            sx={{ mb: 2 }}
          >
            Polimorphic supports a wide range of languages. Simply ask in your language and we'll
            translate the response.
          </Typography>
          <Typography
            fontFamily={orgAiSettings.font_family || 'inherit'}
            variant='body1'
            sx={{ mb: 2 }}
          >
            Here are the languages we support:
          </Typography>
          <List>
            {SUPPORTED_LANGUAGES.map((lang) => (
              <ListItem
                key={lang.code}
                button
                onClick={() => {
                  translateGreeting(lang.code)
                  setLanguageDrawerOpen(false)
                }}
                selected={selectedLanguage === lang.code}
              >
                <ListItemText
                  primary={lang.english}
                  secondary={lang.native}
                  primaryTypographyProps={{
                    sx: { fontFamily: orgAiSettings.font_family || 'inherit' },
                  }}
                  secondaryTypographyProps={{
                    sx: { fontFamily: orgAiSettings.font_family || 'inherit' },
                  }}
                />
              </ListItem>
            ))}
          </List>
        </Box>
      </Drawer>
    </Box>
  )
}

export default AIInteractionView
